import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from '@store/slices/authSlice'
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Card,
  CardContent,
  Grid,
  Stack,
  Divider,
  useMediaQuery,
  Avatar,
  Paper,
  LinearProgress,
  CircularProgress
} from '@mui/material'
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as UsageIcon,
  Language as LanguageIcon,
  TrendingUp
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

const Users = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const token = useSelector(selectToken)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    plan: ''
  })
  const [selectedUser, setSelectedUser] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)

  useEffect(() => {
    console.log('🔄 useEffect triggered - fetching users...')
    console.log('📄 Page:', pagination.page, 'PageSize:', pagination.pageSize)
    console.log('🔍 Filters:', filters)
    
    const timer = setTimeout(() => {
      fetchUsers()
    }, 100)
    
    return () => clearTimeout(timer)
  }, [pagination.page, pagination.pageSize, filters])

  const fetchUsers = async () => {
    try {
      console.log('🔄 Fetching users...')
      setLoading(true)
      setError(null)

      if (!token) {
        setError('Authentication token not found')
        setLoading(false)
        return
      }
      
      const params = new URLSearchParams({
        page: pagination.page + 1,
        limit: pagination.pageSize,
        ...filters
      })

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/admin/users?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
      console.log('📡 Users API response:', response)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const result = await response.json()
      console.log('📊 Users data received:', result)
      
      if (result.success) {
        const transformedUsers = transformUserData(result.users || [])
        console.log('📊 Setting users data:', transformedUsers.length, 'users')
        setUsers(transformedUsers)
        setPagination(prev => ({
          ...prev,
          total: result.pagination?.total || 0
        }))
        setError(null) // Clear any previous errors
      } else {
        console.error('❌ Users API returned error:', result.message)
        setError(result.message || 'Failed to fetch users')
        setUsers([])
        setPagination(prev => ({
          ...prev,
          total: 0
        }))
      }
    } catch (error) {
      console.error('❌ Failed to fetch users:', error)
      
      if (error.message.includes('401')) {
        setError('Authentication failed. Please login again.')
      } else {
        setError(error.message || 'Failed to fetch users. Please try again.')
      }
      
      setUsers([])
      setPagination(prev => ({
        ...prev,
        total: 0
      }))
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPagination(prev => ({ ...prev, page: 0 }))
  }

  const clearFilters = () => {
    setFilters({ search: '', status: '', plan: '' })
    setPagination(prev => ({ ...prev, page: 0 }))
  }

  const handleViewUser = (user) => {
    setSelectedUser(user)
    setViewDialogOpen(true)
  }

  const handleDeleteUser = async (userId) => {
    try {
      console.log('🗑️ Attempting to delete user with ID:', userId)
      
      if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
        return
      }

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const deleteUrl = `${apiUrl}/admin/users/${userId}`
      console.log('🔗 Delete URL:', deleteUrl)
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      })

      console.log('📡 Delete response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ Delete API error:', errorText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('📊 Delete result:', result)
      
      if (result.success) {
        // Remove ONLY the specific user from local state
        console.log('✅ Removing user from local state:', userId)
        setUsers(prev => {
          const filteredUsers = prev.filter(user => user.id !== userId)
          console.log('📋 Users before filter:', prev.length)
          console.log('📋 Users after filter:', filteredUsers.length)
          return filteredUsers
        })
        
        setPagination(prev => ({
          ...prev,
          total: prev.total - 1
        }))
        
        console.log('✅ User deleted successfully')
        alert('User deleted successfully!')
      } else {
        throw new Error(result.message || 'Failed to delete user')
      }
    } catch (error) {
      console.error('❌ Failed to delete user:', error)
      alert('Failed to delete user: ' + error.message)
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success'
      case 'inactive': return 'default'
      case 'suspended': return 'error'
      default: return 'default'
    }
  }

  const getPlanColor = (planType) => {
    switch (planType?.toLowerCase()) {
      case 'enterprise': return 'error'
      case 'pro': return 'warning'
      case 'basic': return 'info'
      case 'free': return 'default'
      default: return 'default'
    }
  }

  // Transform API data to match component structure
  const transformUserData = (users) => {
    return users.map(user => ({
      id: user.id,
      phone: user.phoneNumber,
      language: user.language,
      status: user.isActive ? 'active' : 'inactive',
      plan: user.subscription?.planType?.toLowerCase() || 'free',
      planType: user.subscription?.planType || 'FREE',
      subscriptionStatus: user.subscription?.status || 'INACTIVE',
      maxReminders: user.subscription?.maxReminders || 0,
      currentUsage: user.subscription?.currentUsage || 0,
      remindersCount: user.remindersCount || 0,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      subscription: user.subscription
    }))
  }

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {params.row.id.slice(0, 8)}...
        </Typography>
      )
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <PhoneIcon fontSize="small" color="primary" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.row.phone}
          </Typography>
        </Box>
      )
    },
    {
      field: 'plan',
      headerName: 'Plan',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.planType}
          size="small"
          color={getPlanColor(params.row.plan)}
          variant="outlined"
          sx={{ fontWeight: 600, minWidth: 60 }}
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={getStatusColor(params.row.status)}
          variant="filled"
          sx={{ fontWeight: 600, minWidth: 60, textTransform: 'capitalize' }}
        />
      )
    },
    {
      field: 'usage',
      headerName: 'Usage',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ width: '100%', pr: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {params.row.currentUsage}/{params.row.maxReminders}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {Math.round((params.row.currentUsage / params.row.maxReminders) * 100 || 0)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(params.row.currentUsage / params.row.maxReminders) * 100 || 0}
            sx={{ 
              height: 6, 
              borderRadius: 3,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3
              }
            }}
          />
        </Box>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {dayjs(params.row.createdAt).format('MMM DD, YYYY')}
          </Typography>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewUser(params.row)}
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.light', color: 'white' }
              }}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete User">
            <IconButton 
              size="small" 
              onClick={() => handleDeleteUser(params.row.id)}
              sx={{ 
                color: 'error.main',
                '&:hover': { bgcolor: 'error.light', color: 'white' }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ]

  return (
    <Box>
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: 4,
          gap: 2
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {t('users.title')}
            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {t('common.manageUsers')}
                </Typography>
            
            {/* Error Display */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, bgcolor: 'error.light', borderRadius: 1 }}>
                                        <Typography variant="body2" color="error.contrastText" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        ⚠️ {t('common.errorLoadingUsers')} {error}. {t('common.showingFallbackUsers')}
                      </Typography>
                </Box>
              </motion.div>
            )}
          </Box>
        </Box>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 3, p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center', 
            flexWrap: 'wrap',
            '& > *': { minWidth: { xs: '100%', sm: 'auto' } }
          }}>
            <TextField
              label={t('common.search')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              size="small"
              sx={{ 
                minWidth: { xs: '100%', sm: 200 },
                flex: { xs: '1 1 100%', sm: '0 0 auto' }
              }}
            />
            
            <FormControl size="small" sx={{ 
              minWidth: { xs: '100%', sm: 120 },
              flex: { xs: '1 1 100%', sm: '0 0 auto' }
            }}>
              <InputLabel>{t('users.status')}</InputLabel>
              <Select
                value={filters.status}
                label={t('users.status')}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="active">{t('status.active')}</MenuItem>
                <MenuItem value="inactive">{t('status.inactive')}</MenuItem>
                <MenuItem value="suspended">{t('status.suspended')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ 
              minWidth: { xs: '100%', sm: 120 },
              flex: { xs: '1 1 100%', sm: '0 0 auto' }
            }}>
              <InputLabel>{t('users.plan')}</InputLabel>
              <Select
                value={filters.plan}
                label={t('users.plan')}
                onChange={(e) => handleFilterChange('plan', e.target.value)}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="free">{t('plans.free')}</MenuItem>
                <MenuItem value="basic">{t('plans.basic')}</MenuItem>
                <MenuItem value="pro">{t('plans.pro')}</MenuItem>
                <MenuItem value="enterprise">{t('plans.enterprise')}</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                flex: { xs: '1 1 100%', sm: '0 0 auto' }
              }}
            >
              {t('common.clear')}
            </Button>
          </Box>
        </Card>
      </motion.div>

      {/* Users Display - DataGrid for Desktop, Cards for Mobile */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {isMobile ? (
          // Mobile Card View
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No users found
                </Typography>
              </Card>
            ) : (
              users.map((user) => (
                <Card key={user.id} sx={{ p: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {user.phone}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          ID: {user.id.slice(0, 8)}...
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleViewUser(user)}>
                          <ViewIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 1.5 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">Plan</Typography>
                          </Box>
                          <Chip
                            label={user.planType}
                            size="small"
                            color={getPlanColor(user.plan)}
                            variant="outlined"
                          />
                        </Stack>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <UsageIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">Status</Typography>
                          </Box>
                          <Chip
                            label={user.status}
                            size="small"
                            color={getStatusColor(user.status)}
                            variant="filled"
                          />
                        </Stack>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUp fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Usage: {user.currentUsage}/{user.maxReminders}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(user.currentUsage / user.maxReminders) * 100 || 0}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Stack>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Created: {dayjs(user.createdAt).format('MMM DD, YYYY')}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
          // Desktop DataGrid View
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <Box sx={{ 
              height: { xs: 400, sm: 500, md: 650 }, 
              width: '100%',
              overflow: 'hidden'
            }}>
              <DataGrid
                rows={users}
                columns={columns}
                loading={loading}
                pagination
                paginationMode="server"
                rowCount={pagination.total}
                page={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                onPageSizeChange={(newPageSize) => setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 0 }))}
                pageSizeOptions={[10, 25, 50, 100]}
                disableRowSelectionOnClick
                disableColumnMenu
                rowHeight={70}
                headerHeight={60}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-main': {
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: theme.palette.grey[50],
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      '& .MuiDataGrid-columnHeader': {
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        '&:focus': {
                          outline: 'none'
                        },
                        '&:focus-within': {
                          outline: 'none'
                        }
                      }
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      '& .MuiDataGrid-row': {
                        '&:nth-of-type(even)': {
                          backgroundColor: theme.palette.grey[25]
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.primary.light + '15',
                          transform: 'translateY(-1px)',
                          boxShadow: `0 2px 8px ${theme.palette.primary.main}25`,
                          transition: 'all 0.2s ease-in-out'
                        },
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.875rem',
                          padding: '8px 12px',
                          '&:focus': {
                            outline: 'none'
                          },
                          '&:focus-within': {
                            outline: 'none'
                          }
                        }
                      }
                    }
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: `2px solid ${theme.palette.grey[200]}`,
                    backgroundColor: theme.palette.grey[50],
                    '& .MuiTablePagination-root': {
                      fontSize: '0.875rem'
                    }
                  },
                  '& .MuiDataGrid-overlay': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(4px)'
                  },
                  '& .MuiLinearProgress-root': {
                    height: 3
                  }
                }}
              />
            </Box>
          </Card>
        )}
      </motion.div>

      {/* User Detail Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">User Details</Typography>
              {selectedUser && (
                <Typography variant="body2" color="text.secondary">
                  {selectedUser.phone}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Basic Info */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Basic Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PhoneIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Phone Number
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedUser.phone}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LanguageIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Language
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedUser.language}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Subscription Info */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Subscription Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PersonIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Plan Type
                          </Typography>
                        </Box>
                        <Chip
                          label={selectedUser.planType}
                          color={getPlanColor(selectedUser.plan)}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <UsageIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Status
                          </Typography>
                        </Box>
                        <Chip
                          label={selectedUser.status}
                          color={getStatusColor(selectedUser.status)}
                          variant="filled"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <TrendingUp fontSize="small" color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Usage Statistics
                          </Typography>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">
                              Reminders Used: {selectedUser.currentUsage}/{selectedUser.maxReminders}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {Math.round((selectedUser.currentUsage / selectedUser.maxReminders) * 100 || 0)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(selectedUser.currentUsage / selectedUser.maxReminders) * 100 || 0}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Timestamps */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Account Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Created At
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          {dayjs(selectedUser.createdAt).format('MMMM DD, YYYY HH:mm')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarIcon fontSize="small" color="primary" />
                          <Typography variant="subtitle2" color="text.secondary">
                            Last Updated
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          {dayjs(selectedUser.updatedAt).format('MMMM DD, YYYY HH:mm')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Additional Stats */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Usage Statistics
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h4" color="primary" fontWeight="bold">
                            {selectedUser.remindersCount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Reminders
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h4" color="success.main" fontWeight="bold">
                            {selectedUser.maxReminders - selectedUser.currentUsage}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Remaining
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h4" color="warning.main" fontWeight="bold">
                            {dayjs().diff(dayjs(selectedUser.createdAt), 'day')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Days Active
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Users
